import type {DashboardConfig} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-types";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";
import {
    fetchAnalyticsData,
    getDataset,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-api";
import {DashboardRowType} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-types";
import {FilterType, type Dataset, type Filter} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import {AnalyticsQueryVariant, type DataResolverProps} from "@vesta/vos-types/modules/workspaceLevel/domain/RawDataType";
import {WidgetType, type Widget} from "~/components/analyticsWidgets/widgetTypes/widget";
import {DhiraaguEventContainerDispositionEnum, dhiraaguEventContainerDispositions} from "~/domainModels/workspaceSpecific/dhiraagu";
import {NumberSystems} from "~/utilities/typeDefinitions";
import {OttopilotEventContainerStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import type {QueryClient} from "@tanstack/react-query";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {DASHBOARD_DATASET_KEYS_BY_WORKSPACE} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-constants";
import {resolveDashboardDatasetsAndColumns} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-helpers";

// const DISP__NEW_LEAD = dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.NEW_LEAD].id;
const DISP__NOT_REACHABLE = dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.NOT_CONNECTED_NOT_REACHABLE].id;
const DISP__CALL_BACK_LATER = dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER].id;
// const DISP__DEAD_LEAD_NOT_CONNECTED = dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.NOT_CONNECTED_DEAD_LEAD].id;
const DISP__INVALID_LEAD_NO_FILL = dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL].id;
const DISP__INVALID_LEAD_MISTAKE = dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE].id;
const DISP__INVALID_LEAD_NO_FILL_DEAD =
    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL_DEAD_LEAD].id;
const DISP__INVALID_LEAD_MISTAKE_DEAD =
    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE_DEAD_LEAD].id;

export function getDhiraaguDashboardConfig({
    datasets,
    intellsysBaseUrl,
    accessToken,
    workspaceId,
    queryClient,
    users,
    userTemplates,
    ouTemplates,
    timeZone,
    startTime,
    endTime,
}: {
    datasets: Array<Dataset>;
    intellsysBaseUrl: string;
    accessToken: string;
    workspaceId: Uuid;
    queryClient: QueryClient;
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    ouTemplates: Array<OrganizationalUnitTemplate>;
    timeZone: Uuid;
    startTime: Iso8601DateTime;
    endTime: Iso8601DateTime;
}): DashboardConfig {
    const dashboardData = resolveDashboardDatasetsAndColumns(datasets, DASHBOARD_DATASET_KEYS_BY_WORKSPACE.dhiraagu);

    // const acDataset = dashboardData.datasets.dataOfDifferentMetrics;
    // const dntDataset = dashboardData.datasets.dashboardNonTimeSeries;
    const dwotDataset = dashboardData.datasets.datasetWithoutTarget;
    const dwtDataset = dashboardData.datasets.datasetWithTargets;
    const fkpiDataset = dashboardData.datasets.enquiryToCallSaleFunnelKpis;
    // const AC = dashboardData.columnIds.dataOfDifferentMetrics;
    // const DNT = dashboardData.columnIds.dashboardNonTimeSeries;
    const DWOT = dashboardData.columnIds.datasetWithoutTarget;
    const DWT = dashboardData.columnIds.datasetWithTargets;
    const FKPI = dashboardData.columnIds.enquiryToCallSaleFunnelKpis;

    const partialGetDataset = (datasetId: Uuid) => getDataset(datasetId, intellsysBaseUrl, accessToken, workspaceId);
    const partialFetchData = (dataset: Dataset, props: DataResolverProps) =>
        fetchAnalyticsData(intellsysBaseUrl, accessToken, workspaceId, queryClient, users, userTemplates, ouTemplates, dashboardData, dataset, props);

    const makeNumberTileWidget = (
        id: Uuid,
        type: WidgetType.numberTile,
        dataset: Dataset,
        title: string | null,
        metrics: Uuid[],
        dimensions: Uuid[],
        queryVariant: AnalyticsQueryVariant,
        valueSuffix: string = "",
        decimalPlaces: number = 0,
    ): Widget => ({
        id,
        type,
        datasetId: dataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions,
        metrics,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant,
        numberSystem: NumberSystems.indian,
        decimalPlaces,
        spreadValue: false,
        valuePrefix: "",
        valueSuffix,
    });

    const makeChartWidget = (
        id: Uuid,
        type:
            | WidgetType.pieChart
            | WidgetType.donutChart
            | WidgetType.multiBarGraph
            | WidgetType.stackedBarGraph
            | WidgetType.horizontalBarGraph
            | WidgetType.lineGraph
            | WidgetType.areaGraph
            | WidgetType.comboChart
            | WidgetType.table,
        dataset: Dataset,
        title: string | null,
        metrics: Uuid[],
        dimensions: Uuid[],
        queryVariant: AnalyticsQueryVariant,
    ): Widget => ({
        id,
        type,
        datasetId: dataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions,
        metrics,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant,
    });

    const makeDispositionTile = (id: Uuid, title: string | null, dispositionIds: string[], status?: OttopilotEventContainerStatus): Widget => ({
        ...makeNumberTileWidget(id, WidgetType.numberTile, dwotDataset, title, [DWOT.eventContainer], [], AnalyticsQueryVariant.periodicAsNonTimeSeries),
        editTimeFilters: [
            {
                type: FilterType.stringIn,
                attribute: DWOT.disposition,
                values: dispositionIds,
            },
            ...(status !== undefined
                ? [
                      {
                          type: FilterType.stringEquals,
                          attribute: DWOT.status,
                          value: status,
                      } satisfies Filter,
                  ]
                : []),
        ],
    });

    const sections: DashboardConfig["sections"] = [];

    sections.push({
        id: uniqueUuid`62deb047-30cb-48a9-9a1e-b7f5a5ceb331`,
        title: "Key Performance Snapshot",
        rows: [
            {
                title: null,
                type: DashboardRowType.numberTilesRow,
                widgets: [
                    makeNumberTileWidget(
                        uniqueUuid`033d0544-fcb2-4593-baf1-5e0a6fa35c1c`,
                        WidgetType.numberTile,
                        dwotDataset,
                        "Total Leads",
                        [DWOT.eventContainer],
                        [],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                    makeNumberTileWidget(
                        uniqueUuid`afe637cf-4e5e-40ab-aed0-8f2085fbfc47`,
                        WidgetType.numberTile,
                        dwotDataset,
                        "Total Enquiries",
                        [DWOT.enquiry],
                        [],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                    makeNumberTileWidget(
                        uniqueUuid`ad0bc7f2-9fb5-4d6c-af21-5427c6858e73`,
                        WidgetType.numberTile,
                        dwotDataset,
                        "Total Calls",
                        [DWOT.calls],
                        [],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                    makeNumberTileWidget(
                        uniqueUuid`53a916d9-cc3f-4cf8-8f6e-b5938f477d56`,
                        WidgetType.numberTile,
                        dwotDataset,
                        "Total Sales",
                        [DWOT.sales],
                        [],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                    makeNumberTileWidget(
                        uniqueUuid`6e70d4cb-771d-4b9c-926d-e658e994a112`,
                        WidgetType.numberTile,
                        dwtDataset,
                        "Total Target Events",
                        [DWT.target],
                        [],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`6352e240-a9ea-4ba0-9c7f-e7ae5e6f0740`,
                        WidgetType.donutChart,
                        dwotDataset,
                        "Lead Status Breakdown",
                        [DWOT.eventContainer],
                        [DWOT.status],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                    makeChartWidget(
                        uniqueUuid`4dec23f8-337f-43b8-bafb-b0cf20d52030`,
                        WidgetType.donutChart,
                        dwtDataset,
                        "Event Status Breakdown",
                        [DWT.btlActivities],
                        [DWT.status],
                        AnalyticsQueryVariant.pointInTimeAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`1688e5a9-d3a0-4211-9414-662179f6c277`,
                        WidgetType.donutChart,
                        dwotDataset,
                        "Sales Status Breakdown",
                        [DWOT.sales],
                        [DWOT.status],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                    makeChartWidget(
                        uniqueUuid`742d91b1-e317-44d9-bbb9-1942d4a6fc81`,
                        WidgetType.donutChart,
                        dwotDataset,
                        "Calls Status Breakdown",
                        [DWOT.calls],
                        [DWOT.status],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                    makeChartWidget(
                        uniqueUuid`aab6f799-f75e-46d7-91c5-70756cfcbb0a`,
                        WidgetType.donutChart,
                        dwotDataset,
                        "Enquiries Status Breakdown",
                        [DWOT.enquiry],
                        [DWOT.status],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
        ],
    });

    sections.push({
        id: uniqueUuid`06736312-f520-4a40-8d1c-e23c533ad349`,
        title: "Disposition Performance Insights",
        rows: [
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`6352e240-a9ea-4ba0-9c7f-e7ae5e6f0740`,
                        WidgetType.multiBarGraph,
                        dwotDataset,
                        "Lead Connectivity Status",
                        [DWOT.eventContainer],
                        [DWOT.disposition],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.numberTilesRow,
                widgets: [
                    makeDispositionTile(uniqueUuid`36c8a396-bc6a-4a75-beb3-5b99f895630b`, "Not Reachable", [DISP__NOT_REACHABLE]),
                    makeDispositionTile(uniqueUuid`fa8b580e-e555-471a-baaa-ca91381d9303`, "Call Back Later", [DISP__CALL_BACK_LATER]),
                    makeDispositionTile(uniqueUuid`09ad698c-b32d-4c4d-a95c-3fee28517257`, "Invalid Lead", [
                        DISP__INVALID_LEAD_NO_FILL,
                        DISP__INVALID_LEAD_MISTAKE,
                    ]),
                    makeDispositionTile(uniqueUuid`4cd475b9-7d50-4b28-81b8-f0cbfc99ccdd`, "Dead Lead", [
                        DISP__INVALID_LEAD_NO_FILL_DEAD,
                        DISP__INVALID_LEAD_MISTAKE_DEAD,
                    ]),
                ],
            },
            {
                title: "Detail Disposition Tree",
                type: DashboardRowType.tabbedLadderSection,
                id: uniqueUuid`a1b2c3d4-e5f6-4789-8abc-def012345678`,
                tabs: [
                    {
                        label: "Count",
                        levelSizes: [1, 3, 4, 6, 6],
                        widgets: [
                            // Level 1 — 1 tile
                            makeDispositionTile(uniqueUuid`f1e2d3c4-b5a6-4978-8a7b-6c5d4e3f2a1b`, "New Lead", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.NEW_LEAD].id,
                            ]),
                            // Level 2 — 3 tiles
                            makeDispositionTile(uniqueUuid`a9b8c7d6-e5f4-4a3b-8c2d-1e0f9a8b7c6d`, "Connected", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER].id,
                                dhiraaguEventContainerDispositions[
                                    DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_PRODUCT_DETAILS_REQUIRED
                                ].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_DEMO_REQUIRED].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_FEASIBILITY_CHECK].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_CUSTOM_PLAN_APPROVAL]
                                    .id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_NOT_ELIGIBLE].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_OTHER].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_PRICE_ISSUE].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_SERVICE_ISSUE].id,
                                dhiraaguEventContainerDispositions[
                                    DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_COMPETITOR_PURCHASE
                                ].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_OTHER].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL_DEAD_LEAD].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE_DEAD_LEAD].id,
                            ]),
                            makeDispositionTile(uniqueUuid`5d4c3b2a-1f0e-4d9c-8b7a-6e5f4d3c2b1a`, "Not Connected", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.NOT_CONNECTED_NOT_REACHABLE].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.NOT_CONNECTED_DEAD_LEAD].id,
                            ]),
                            makeDispositionTile(uniqueUuid`3c2b1a0f-9e8d-4c7b-6a5f-4e3d2c1b0a9f`, "Wrong Number", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.WRONG_NUMBER].id,
                            ]),
                            // Level 3 — 4 tiles
                            makeDispositionTile(uniqueUuid`8e7d6c5b-4a39-4f28-b17a-06f5e4d3c2b1`, "Valid Lead", [
                                dhiraaguEventContainerDispositions[
                                    DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_PRODUCT_DETAILS_REQUIRED
                                ].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_DEMO_REQUIRED].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_FEASIBILITY_CHECK].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_CUSTOM_PLAN_APPROVAL]
                                    .id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_NOT_ELIGIBLE].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_OTHER].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_PRICE_ISSUE].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_SERVICE_ISSUE].id,
                                dhiraaguEventContainerDispositions[
                                    DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_COMPETITOR_PURCHASE
                                ].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_OTHER].id,
                            ]),
                            makeDispositionTile(uniqueUuid`7f6e5d4c-3b2a-4190-80ff-9e8d7c6b5a4f`, "Call Back Later", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER].id,
                            ]),
                            makeDispositionTile(uniqueUuid`6a5f4e3d-2c1b-40af-9f8e-7d6c5b4a3f2e`, "Invalid Lead", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL_DEAD_LEAD].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE_DEAD_LEAD].id,
                            ]),
                            makeDispositionTile(uniqueUuid`5b4a3f2e-1d0c-49be-8f7e-6d5c4b3a2f1e`, "Did Not Fill", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL_DEAD_LEAD].id,
                            ]),
                            // Level 4 — 6 tiles
                            makeDispositionTile(uniqueUuid`4c3b2a1f-0e9d-48cd-7e6f-5d4c3b2a1f0e`, "Interested", [
                                dhiraaguEventContainerDispositions[
                                    DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_PRODUCT_DETAILS_REQUIRED
                                ].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_DEMO_REQUIRED].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_FEASIBILITY_CHECK].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_CUSTOM_PLAN_APPROVAL]
                                    .id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_NOT_ELIGIBLE].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_OTHER].id,
                            ]),
                            makeDispositionTile(uniqueUuid`3d2c1b0a-9f8e-47bc-6d5e-4c3b2a1f0e9d`, "Not Interested", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_PRICE_ISSUE].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_SERVICE_ISSUE].id,
                                dhiraaguEventContainerDispositions[
                                    DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_COMPETITOR_PURCHASE
                                ].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_OTHER].id,
                            ]),
                            makeDispositionTile(uniqueUuid`2e1d0c9b-8a7f-46ab-5c4d-3b2a1f0e9d8c`, "Price Issues", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_PRICE_ISSUE].id,
                            ]),
                            makeDispositionTile(uniqueUuid`1f0e9d8c-7b6a-459a-4b3c-2a1f0e9d8c7b`, "Service Issues", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_SERVICE_ISSUE].id,
                            ]),
                            makeDispositionTile(uniqueUuid`0a9f8e7d-6c5b-4489-3a2b-1f0e9d8c7b6a`, "Filled By Mistake", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE_DEAD_LEAD].id,
                            ]),
                            makeDispositionTile(uniqueUuid`9b8a7f6e-5d4c-4378-2f1a-0e9d8c7b6a5f`, "Competitor Purchase", [
                                dhiraaguEventContainerDispositions[
                                    DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_COMPETITOR_PURCHASE
                                ].id,
                            ]),
                            // Level 5 — 6 tiles
                            makeDispositionTile(uniqueUuid`8c7b6a5f-4e3d-4267-1e0f-9d8c7b6a5f4e`, "Product Details Required", [
                                dhiraaguEventContainerDispositions[
                                    DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_PRODUCT_DETAILS_REQUIRED
                                ].id,
                            ]),
                            makeDispositionTile(uniqueUuid`7d6c5b4a-3f2e-4156-0d9e-8c7b6a5f4e3d`, "Demo Required", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_DEMO_REQUIRED].id,
                            ]),
                            makeDispositionTile(uniqueUuid`6e5d4c3b-2a1f-4045-9c8d-7b6a5f4e3d2c`, "Feasibility Check", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_FEASIBILITY_CHECK].id,
                            ]),
                            makeDispositionTile(uniqueUuid`5f4e3d2c-1b0a-49f4-8b7c-6a5f4e3d2c1b`, "Custom Plan Approval", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_CUSTOM_PLAN_APPROVAL]
                                    .id,
                            ]),
                            makeDispositionTile(uniqueUuid`4a3f2e1d-0c9b-48e3-7a6b-5f4e3d2c1b0a`, "Not Eligible", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_NOT_ELIGIBLE].id,
                            ]),
                            makeDispositionTile(uniqueUuid`3b2a1f0e-9d8c-47d2-6f5a-4e3d2c1b0a9f`, "Others", [
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_OTHER].id,
                                dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_OTHER].id,
                            ]),
                        ],
                    },
                    {
                        label: "Active",
                        levelSizes: [1, 3, 4, 6, 6],
                        widgets: [
                            // Level 1 — 1 tile
                            makeDispositionTile(
                                uniqueUuid`f1e2d3c4-b5a6-4978-8a7b-6c5d4e3f2a1b`,
                                "New Lead",
                                [dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.NEW_LEAD].id],
                                OttopilotEventContainerStatus.active,
                            ),
                            // Level 2 — 3 tiles
                            makeDispositionTile(
                                uniqueUuid`a9b8c7d6-e5f4-4a3b-8c2d-1e0f9a8b7c6d`,
                                "Connected",
                                [
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER].id,
                                    dhiraaguEventContainerDispositions[
                                        DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_PRODUCT_DETAILS_REQUIRED
                                    ].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_DEMO_REQUIRED].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_FEASIBILITY_CHECK]
                                        .id,
                                    dhiraaguEventContainerDispositions[
                                        DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_CUSTOM_PLAN_APPROVAL
                                    ].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_NOT_ELIGIBLE].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_OTHER].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_PRICE_ISSUE]
                                        .id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_SERVICE_ISSUE]
                                        .id,
                                    dhiraaguEventContainerDispositions[
                                        DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_COMPETITOR_PURCHASE
                                    ].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_OTHER].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL_DEAD_LEAD].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE_DEAD_LEAD]
                                        .id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`5d4c3b2a-1f0e-4d9c-8b7a-6e5f4d3c2b1a`,
                                "Not Connected",
                                [
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.NOT_CONNECTED_NOT_REACHABLE].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.NOT_CONNECTED_DEAD_LEAD].id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`3c2b1a0f-9e8d-4c7b-6a5f-4e3d2c1b0a9f`,
                                "Wrong Number",
                                [dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.WRONG_NUMBER].id],
                                OttopilotEventContainerStatus.active,
                            ),
                            // Level 3 — 4 tiles
                            makeDispositionTile(
                                uniqueUuid`8e7d6c5b-4a39-4f28-b17a-06f5e4d3c2b1`,
                                "Valid Lead",
                                [
                                    dhiraaguEventContainerDispositions[
                                        DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_PRODUCT_DETAILS_REQUIRED
                                    ].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_DEMO_REQUIRED].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_FEASIBILITY_CHECK]
                                        .id,
                                    dhiraaguEventContainerDispositions[
                                        DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_CUSTOM_PLAN_APPROVAL
                                    ].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_NOT_ELIGIBLE].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_OTHER].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_PRICE_ISSUE]
                                        .id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_SERVICE_ISSUE]
                                        .id,
                                    dhiraaguEventContainerDispositions[
                                        DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_COMPETITOR_PURCHASE
                                    ].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_OTHER].id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`7f6e5d4c-3b2a-4190-80ff-9e8d7c6b5a4f`,
                                "Call Back Later",
                                [dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER].id],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`6a5f4e3d-2c1b-40af-9f8e-7d6c5b4a3f2e`,
                                "Invalid Lead",
                                [
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL_DEAD_LEAD].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE_DEAD_LEAD]
                                        .id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`5b4a3f2e-1d0c-49be-8f7e-6d5c4b3a2f1e`,
                                "Did Not Fill",
                                [
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_DID_NOT_FILL_DEAD_LEAD].id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            // Level 4 — 6 tiles
                            makeDispositionTile(
                                uniqueUuid`4c3b2a1f-0e9d-48cd-7e6f-5d4c3b2a1f0e`,
                                "Interested",
                                [
                                    dhiraaguEventContainerDispositions[
                                        DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_PRODUCT_DETAILS_REQUIRED
                                    ].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_DEMO_REQUIRED].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_FEASIBILITY_CHECK]
                                        .id,
                                    dhiraaguEventContainerDispositions[
                                        DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_CUSTOM_PLAN_APPROVAL
                                    ].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_NOT_ELIGIBLE].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_OTHER].id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`3d2c1b0a-9f8e-47bc-6d5e-4c3b2a1f0e9d`,
                                "Not Interested",
                                [
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_PRICE_ISSUE]
                                        .id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_SERVICE_ISSUE]
                                        .id,
                                    dhiraaguEventContainerDispositions[
                                        DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_COMPETITOR_PURCHASE
                                    ].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_OTHER].id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`2e1d0c9b-8a7f-46ab-5c4d-3b2a1f0e9d8c`,
                                "Price Issues",
                                [dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_PRICE_ISSUE].id],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`1f0e9d8c-7b6a-459a-4b3c-2a1f0e9d8c7b`,
                                "Service Issues",
                                [
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_SERVICE_ISSUE]
                                        .id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`0a9f8e7d-6c5b-4489-3a2b-1f0e9d8c7b6a`,
                                "Filled By Mistake",
                                [
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_INVALID_LEAD_FILLED_BY_MISTAKE_DEAD_LEAD]
                                        .id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`9b8a7f6e-5d4c-4378-2f1a-0e9d8c7b6a5f`,
                                "Competitor Purchase",
                                [
                                    dhiraaguEventContainerDispositions[
                                        DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_COMPETITOR_PURCHASE
                                    ].id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            // Level 5 — 6 tiles
                            makeDispositionTile(
                                uniqueUuid`8c7b6a5f-4e3d-4267-1e0f-9d8c7b6a5f4e`,
                                "Product Details Required",
                                [
                                    dhiraaguEventContainerDispositions[
                                        DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_PRODUCT_DETAILS_REQUIRED
                                    ].id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`7d6c5b4a-3f2e-4156-0d9e-8c7b6a5f4e3d`,
                                "Demo Required",
                                [dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_DEMO_REQUIRED].id],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`6e5d4c3b-2a1f-4045-9c8d-7b6a5f4e3d2c`,
                                "Feasibility Check",
                                [
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_FEASIBILITY_CHECK]
                                        .id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`5f4e3d2c-1b0a-49f4-8b7c-6a5f4e3d2c1b`,
                                "Custom Plan Approval",
                                [
                                    dhiraaguEventContainerDispositions[
                                        DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_CUSTOM_PLAN_APPROVAL
                                    ].id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`4a3f2e1d-0c9b-48e3-7a6b-5f4e3d2c1b0a`,
                                "Not Eligible",
                                [dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_NOT_ELIGIBLE].id],
                                OttopilotEventContainerStatus.active,
                            ),
                            makeDispositionTile(
                                uniqueUuid`3b2a1f0e-9d8c-47d2-6f5a-4e3d2c1b0a9f`,
                                "Others",
                                [
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_INTERESTED_OTHER].id,
                                    dhiraaguEventContainerDispositions[DhiraaguEventContainerDispositionEnum.CONNECTED_VALID_LEAD_NOT_INTERESTED_OTHER].id,
                                ],
                                OttopilotEventContainerStatus.active,
                            ),
                        ],
                    },
                ],
            },
        ],
    });

    sections.push({
        id: uniqueUuid`b1c4e2f0-3a5d-4e7b-9c8f-0d2e4f6a8b1c`,
        title: "Lifecycle Performance Overview",
        rows: [
            {
                title: null,
                type: DashboardRowType.numberTilesRow,
                widgets: [
                    makeNumberTileWidget(
                        uniqueUuid`c2d5f3a1-4b6e-4f8c-0d9a-1e3f5a7b9c2d`,
                        WidgetType.numberTile,
                        fkpiDataset,
                        "Enquiry → Call",
                        [FKPI.enquiryToCallPercent],
                        [],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                        "%",
                        1,
                    ),
                    makeNumberTileWidget(
                        uniqueUuid`d3e6a4b2-5c7f-4a9d-1e0b-2f4a6b8c0d3e`,
                        WidgetType.numberTile,
                        fkpiDataset,
                        "Call → Sale",
                        [FKPI.callToSalePercent],
                        [],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                        "%",
                        1,
                    ),
                    makeNumberTileWidget(
                        uniqueUuid`e4f7b5c3-6d8a-4b0e-2f1c-3a5b7c9d1e4f`,
                        WidgetType.numberTile,
                        fkpiDataset,
                        "Enquiry → Sale",
                        [FKPI.enquiryToSalePercent],
                        [],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                        "%",
                        1,
                    ),
                    makeNumberTileWidget(
                        uniqueUuid`7bc7566f-f251-481d-a7a1-9e8ea3117d16`,
                        WidgetType.numberTile,
                        dwtDataset,
                        "Average conversion time",
                        [DWT.avgConversionTime],
                        [],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                        " days",
                    ),
                    makeNumberTileWidget(
                        uniqueUuid`f5a8c6d4-7e9b-4c1f-3a2d-4b6c8d0e2f5a`,
                        WidgetType.numberTile,
                        dwotDataset,
                        "Drop - off",
                        [DWOT.enquiryToCallsDropOffPercent, DWOT.calls, DWOT.enquiry],
                        [],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                        "%",
                        1,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`ad875add-ba3f-46be-9e99-f3f6fdcfbcc9`,
                        WidgetType.stackedBarGraph,
                        dwotDataset,
                        "Product Performance Across Lifecycle",
                        [DWOT.enquiry, DWOT.calls, DWOT.sales],
                        [DWOT.productCatalogue],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`4f5c0fe3-63d3-45ee-b31f-f21912472be2`,
                        WidgetType.multiBarGraph,
                        dwotDataset,
                        "Event Template Performances Across Lifestyle",
                        [DWOT.enquiry, DWOT.calls, DWOT.sales],
                        [DWOT.btlActivityTemplate],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`4d1899f3-3652-4018-8594-a769ef9e42ab`,
                        WidgetType.stackedBarGraph,
                        dwotDataset,
                        "Role wise  Performance Across Lifecycle",
                        [DWOT.enquiry, DWOT.calls, DWOT.sales],
                        [DWOT.userTemplate],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`e6f7a8b9-c0d1-4e2f-b3a4-c5d6e7f8a9b0`,
                        WidgetType.donutChart,
                        fkpiDataset,
                        "Stage-wise Drop-off",
                        [FKPI.enquiryToCallPercent, FKPI.callToSalePercent, FKPI.enquiryToSalePercent],
                        [],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`b1c2d3e4-f5a6-4b7c-8d9e-0f1a2b3c4d5e`,
                        WidgetType.multiBarGraph,
                        dwotDataset,
                        "Geo wise Performance Across Lifecycle",
                        [DWOT.enquiry, DWOT.calls, DWOT.sales],
                        [DWOT.region],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
        ],
    });

    sections.push({
        id: uniqueUuid`c1d2e3f4-a5b6-4c7d-8e9f-0a1b2c3d4e5f`,
        title: "Targets & Achievement",
        rows: [
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`f6a7b8c9-d0e1-4f2a-b3c4-d5e6f7a8b9c0`,
                        WidgetType.multiBarGraph,
                        dwtDataset,
                        "Targets & Achievement Breakdown by Role ",
                        [DWT.target, DWT.achievement],
                        [DWT.userTemplate],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`41cfcdea-20d5-45c1-b4de-8a333a10b819`,
                        WidgetType.multiBarGraph,
                        dwtDataset,
                        "Targets & Achievement Breakdown by Lifecycle stages ",
                        [DWT.target, DWT.achievement],
                        [DWT.eventTemplate],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`f6a7b8c9-d0e1-4f2a-b3c4-d5e6f7a8b9c0`,
                        WidgetType.multiBarGraph,
                        dwtDataset,
                        "Targets & Achievement Breakdown by Event Template ",
                        [DWT.target, DWT.achievement],
                        [DWT.btlActivityTemplate],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`f6a7b8c9-d0e1-4f2a-b3c4-d5e6f7a8b9c0`,
                        WidgetType.multiBarGraph,
                        dwtDataset,
                        "Targets & Achievement Breakdown by Product ",
                        [DWT.target, DWT.achievement],
                        [DWT.product],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d`,
                        WidgetType.multiBarGraph,
                        dwtDataset,
                        "Targets & Achievement Breakdown by Geo",
                        [DWT.target, DWT.achievement],
                        [DWT.region],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.tableRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`c7c98a6e-d781-4ca0-a369-4eb573fcf4ea`,
                        WidgetType.table,
                        dwtDataset,
                        "Execution Performance Overview",
                        [DWT.target, DWT.achievement, DWT.achievementTargetPercentage],
                        [DWT.atoll],
                        AnalyticsQueryVariant.pointInTimeAsNonTimeSeries,
                    ),
                ],
            },
        ],
    });

    sections.push({
        id: uniqueUuid`d3e4f5a6-b7c8-4d9e-af0b-c1d2e3f4a5b6`,
        title: "Beat Execution &  Event effectiveness",
        rows: [
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`e7f8a9b0-c1d2-4e3f-a4b5-c6d7e8f9a0b1`,
                        WidgetType.horizontalBarGraph,
                        dwtDataset,
                        "Beat Execution Status breakdown",
                        [DWT.btlActivities],
                        [DWT.status],
                        AnalyticsQueryVariant.pointInTimeAsNonTimeSeries,
                    ),
                ],
            },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`e7f8a9b0-c1d2-4e3f-a4b5-c6d7e8f9a0b1`,
                        WidgetType.multiBarGraph,
                        dwotDataset,
                        "Beat Events by Template",
                        [DWOT.leadCaptureBooth, DWOT.roadshow, DWOT.referralDriveCampaign, DWOT.dealerFootfallBooster, DWOT.doorToDoor],
                        [],
                        AnalyticsQueryVariant.periodicAsNonTimeSeries,
                    ),
                ],
            },
            // {
            //     title: null,
            //     type: DashboardRowType.chartsRow,
            //     widgets: [
            //         makeChartWidget(
            //             uniqueUuid`e7f8a9b0-c1d2-4e3f-a4b5-c6d7e8f9a0b1`,
            //             WidgetType.multiBarGraph,
            //             dwtDataset,
            //             "Performance by Geo",
            //             [DWT.btlActivities],
            //             [DWT.region],
            //             AnalyticsQueryVariant.periodicAsNonTimeSeries,
            //         ),
            //     ],
            // },
            // {
            //     title: null,
            //     type: DashboardRowType.chartsRow,
            //     widgets: [
            //         makeChartWidget(
            //             uniqueUuid`e7f8a9b0-c1d2-4e3f-a4b5-c6d7e8f9a0b1`,
            //             WidgetType.multiBarGraph,
            //             dwotDataset,
            //             "Geo-wise BTL Manpower Utilization",
            //         ),
            //     ],
            // },
            {
                title: null,
                type: DashboardRowType.chartsRow,
                widgets: [
                    makeChartWidget(
                        uniqueUuid`e7f8a9b0-c1d2-4e3f-a4b5-c6d7e8f9a0b1`,
                        WidgetType.multiBarGraph,
                        dwtDataset,
                        "User Engagement Overview",
                        [DWT.btlExecutorAssignmentsCount, DWT.btlLinkedSalesCount],
                        [DWT.btlActivityTemplate],
                        AnalyticsQueryVariant.pointInTimeAsNonTimeSeries,
                    ),
                ],
            },
        ],
    });

    const dashboardConfig: DashboardConfig = {
        id: uniqueUuid`a0d86456-e652-4dc8-ae59-b5ca123e3c7d`,
        title: "Performance Dashboard",
        sections,
    };

    return dashboardConfig;
}
